import { useMemo, useCallback } from 'react';
import { useGetCustomFieldDefinitions, useGetUserCustomFields } from '../../query/resource-hooks/customFields';
import { CustomFieldDefinition, UserCustomFieldValue } from '../../types/customFields';

export interface UseCustomFieldsDataReturn {
    definitions: CustomFieldDefinition[];
    userFields: UserCustomFieldValue[];
    warnings: string[];
    isLoading: boolean;
    isError: boolean;
    error: any;
    refetch: () => void;
}

export interface UseCustomFieldsDataOptions {
    enabled?: boolean;
}

/**
 * Hook for fetching and managing custom fields data
 * Combines field definitions and user field values
 */
export const useCustomFieldsData = (userId: number, options: UseCustomFieldsDataOptions = {}): UseCustomFieldsDataReturn => {
    const { enabled = true } = options;

    const shouldFetchUserFields = enabled && !!userId;

    // Fetch field definitions (always enabled - they're global)
    const {
        data: definitionsResponse,
        isLoading: definitionsLoading,
        isError: definitionsError,
        error: definitionsErrorData,
        refetch: refetchDefinitions,
    } = useGetCustomFieldDefinitions();

    // Fetch user custom fields (conditionally enabled)
    const {
        data: userFieldsResponse,
        isLoading: userFieldsLoading,
        isError: userFieldsError,
        error: userFieldsErrorData,
        refetch: refetchUserFields,
    } = useGetUserCustomFields(userId, shouldFetchUserFields);

    // Memoized computed values to prevent unnecessary re-renders
    const definitions = useMemo(() => {
        return definitionsResponse?.data ?? [];
    }, [definitionsResponse?.data]);

    const userFields = useMemo(() => {
        return userFieldsResponse?.data ?? [];
    }, [userFieldsResponse?.data]);

    const warnings = useMemo(() => {
        return userFieldsResponse?.warnings ?? [];
    }, [userFieldsResponse?.warnings]);

    // Memoized combined states to prevent unnecessary re-renders
    const isLoading = useMemo(() => {
        return definitionsLoading || (shouldFetchUserFields && userFieldsLoading);
    }, [definitionsLoading, userFieldsLoading, shouldFetchUserFields]);

    const isError = useMemo(() => {
        return definitionsError || (shouldFetchUserFields && userFieldsError);
    }, [definitionsError, userFieldsError, shouldFetchUserFields]);

    const error = useMemo(() => {
        return definitionsErrorData || userFieldsErrorData;
    }, [definitionsErrorData, userFieldsErrorData]);

    // Memoized refetch function to prevent unnecessary re-renders in parent components
    const refetch = useCallback(() => {
        refetchDefinitions();
        if (shouldFetchUserFields) {
            refetchUserFields();
        }
    }, [refetchDefinitions, refetchUserFields, shouldFetchUserFields]);

    return {
        definitions,
        userFields,
        warnings,
        isLoading,
        isError,
        error,
        refetch,
    };
};

export default useCustomFieldsData;
